import React, { useMemo } from 'react';
import { BarChart, PieChart } from 'lucide-react';

interface Interview {
  id: string;
  custom_field_values: Record<string, any>;
}

interface CustomField {
  id: string;
  name: string;
  field_type: string;
  options?: string[];
}

interface InterviewAnalyticsProps {
  interviews: Interview[];
  customFields: CustomField[];
}

export function InterviewAnalytics({ interviews, customFields }: InterviewAnalyticsProps) {
  const analytics = useMemo(() => {
    const results: Record<string, any> = {};

    customFields.forEach(field => {
      const fieldData = {
        name: field.name,
        type: field.field_type,
        values: {} as Record<string | number, number>
      };

      interviews.forEach(interview => {
        const value = interview.custom_field_values[field.id];
        
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            // Handle multi-select fields
            value.forEach(v => {
              fieldData.values[v] = (fieldData.values[v] || 0) + 1;
            });
          } else if (field.field_type === 'boolean') {
            // Convert boolean to Yes/No
            const boolValue = value ? 'Yes' : 'No';
            fieldData.values[boolValue] = (fieldData.values[boolValue] || 0) + 1;
          } else {
            // Handle single values
            fieldData.values[value] = (fieldData.values[value] || 0) + 1;
          }
        }
      });

      results[field.id] = fieldData;
    });

    return results;
  }, [interviews, customFields]);

  if (customFields.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">
          No custom fields defined yet. Add custom fields to see analytics.
        </p>
      </div>
    );
  }

  if (interviews.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">
          No interviews recorded yet. Analytics will appear here once you have interviews.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {Object.entries(analytics).map(([fieldId, data]) => {
        const field = customFields.find(f => f.id === fieldId);
        if (!field) return null;

        const totalResponses = Object.values(data.values).reduce((a, b) => a + b, 0);
        const entries = Object.entries(data.values).sort((a, b) => b[1] - a[1]);

        return (
          <div key={fieldId} className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              {field.field_type === 'single_select' || field.field_type === 'multi_select' ? (
                <PieChart className="h-5 w-5 text-brand-teal mr-2" />
              ) : (
                <BarChart className="h-5 w-5 text-brand-teal mr-2" />
              )}
              <h3 className="text-lg font-medium text-brand-teal">{field.name}</h3>
            </div>

            <div className="space-y-4">
              {entries.map(([value, count]) => {
                const percentage = Math.round((count / totalResponses) * 100);
                
                return (
                  <div key={value} className="relative">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {value}
                      </span>
                      <span className="text-sm text-gray-500">
                        {count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-brand-orange h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 text-sm text-gray-500">
              Total responses: {totalResponses}
            </div>
          </div>
        );
      })}
    </div>
  );
}